import { CommonModule } from "@angular/common";
import { Component, Injector, Input } from "@angular/core";
import { NgZorroAntdModule } from "../../../core/shared/ng-zorro-antd.module";

/**
 * A dynamic stepper component that supports multiple steps, sharing data between steps,
 * and injecting step-specific and shared data using Angular's dependency injection.
 */
@Component({
  selector: "app-stepper",
  standalone: true,
  imports: [CommonModule, NgZorroAntdModule],
  templateUrl: "./stepper.component.html",
  styleUrl: "./stepper.component.scss",
})
export class StepperComponent {
  /**
   * Array of step configurations.
   * Each step can have its own data and logic provided through dependency injection.
   */
  @Input() steps: any[] = [];

  current = 0;
  stepInjectors: Injector[] = [];
  sharedData: any = {}; // Store data shared between steps

  /**
   * Initializes the component and creates injectors for each step.
   */
  constructor() {}

  /**
   * Angular lifecycle hook called after the component is initialized.
   * Prepares injectors for steps with step-specific and shared data.
   */
  ngOnInit(): void {
    this.createInjectors();
  }

  /**
   * Creates injectors for each step, providing step-specific data and shared data.
   * Also includes a mechanism to update shared data dynamically.
   */
  createInjectors(): void {
    this.stepInjectors = this.steps.map((step, index) => {
      return Injector.create({
        providers: [
          { provide: "STEP_DATA", useValue: step.data },
          { provide: "SHARED_DATA", useValue: this.sharedData },
          {
            provide: "STEP_UPDATE",
            useValue: (updatedData: any) =>
              this.updateSharedData(index, updatedData),
          },
        ],
      });
    });
  }

  /**
   * Updates the shared data object with data from a specific step.
   * Also recreates injectors to reflect the updated shared data.
   * @param stepIndex The index of the step providing the updated data.
   * @param data The data to merge into the shared data object.
   */
  updateSharedData(stepIndex: number, data: any): void {
    this.sharedData = { ...this.sharedData, [`step${stepIndex}`]: data };
    console.log("Shared Data Updated:", this.sharedData);
    this.createInjectors(); // Recreate injectors to include updated shared data
  }

  /**
   * Advances to the next step in the stepper if it is not the last step.
   */
  next(): void {
    if (this.current < this.steps.length - 1) {
      this.current += 1;
    }
  }

  /**
   * Moves back to the previous step in the stepper if it is not the first step.
   */
  previous(): void {
    if (this.current > 0) {
      this.current -= 1;
    }
  }

  /**
   * Completes the stepper process and logs the final shared data.
   */
  done(): void {
    console.log("Stepper completed!", this.sharedData);
  }
}
